import { CircleHollowIcon } from 'foundations-assets/icons/components/CircleHollowIcon';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { Toggle } from 'foundations-components/transitional/components/Toggle';
import { filterProps } from 'foundations-components/utils/filterProps';
import styled, { isCanvasTheme, useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
const TOGGLE_WIDTH = 44;
const TOGGLE_HEIGHT = 24;
const THUMB_SIZE = 18;
const THUMB_OFFSET = 3;
const StyledToggleTrack = styled.button.withConfig({
  displayName: "ToggleUI__StyledToggleTrack"
})(({
  $cssStyles
}) => {
  return Object.assign({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    width: `${TOGGLE_WIDTH}px`,
    minWidth: `${TOGGLE_WIDTH}px`,
    height: `${TOGGLE_HEIGHT}px`,
    border: 'none',
    borderRadius: `${TOGGLE_HEIGHT / 2}px`,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out'
  }, $cssStyles);
});
const ToggleTrack = props => {
  const theme = useTheme();
  const isCanvas = isCanvasTheme(theme);

  // defaults
  const backgroundColor = props.checked ? theme.color['icon-interactive-default'] : isCanvas ? theme.color['icon-interactive-pressed'] : theme.color['icon-interactive-on-fill-pressed'];
  const {
    domProps,
    tokenProps,
    callbackProps
  } = filterProps(props);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  return /*#__PURE__*/_jsx(StyledToggleTrack, Object.assign({}, domProps, callbackProps, {
    $cssStyles: Object.assign({
      backgroundColor
    }, cssStyles),
    children: props.children
  }));
};
const ToggleThumb = styled.span.withConfig({
  displayName: "ToggleUI__ToggleThumb"
})(({
  $checked,
  theme
}) => ({
  position: 'absolute',
  top: `${THUMB_OFFSET}px`,
  left: $checked ? `${TOGGLE_WIDTH - THUMB_SIZE - THUMB_OFFSET}px` : `${THUMB_OFFSET}px`,
  width: `${THUMB_SIZE}px`,
  height: `${THUMB_SIZE}px`,
  borderRadius: '50%',
  transition: 'left 0.2s ease-in-out',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.color['text-interactive-on-fill']
}));
const ToggleUI = ({
  id,
  enabled,
  onToggleChange,
  variant = 'default',
  backgroundColor
}) => {
  switch (variant) {
    case 'rounded':
      return /*#__PURE__*/_jsx(ToggleTrack, {
        id: id,
        type: "button",
        role: "switch",
        "aria-checked": enabled,
        onClick: onToggleChange,
        checked: enabled,
        backgroundColor: backgroundColor,
        children: /*#__PURE__*/_jsx(ToggleThumb, {
          $checked: enabled,
          "aria-hidden": "true",
          children: /*#__PURE__*/_jsx(IconCircle, {
            icon: SuccessIcon,
            borderWidth: "0",
            size: "300",
            color: enabled ? 'icon-interactive-default' : 'icon-interactive-pressed'
          })
        })
      });
    case 'rounded-hollow':
      return /*#__PURE__*/_jsx(ToggleTrack, {
        id: id,
        type: "button",
        role: "switch",
        "aria-checked": enabled,
        onClick: onToggleChange,
        checked: enabled,
        backgroundColor: backgroundColor,
        children: /*#__PURE__*/_jsx(ToggleThumb, {
          $checked: enabled,
          "aria-hidden": "true",
          children: /*#__PURE__*/_jsx(CircleHollowIcon, {
            size: "300",
            color: "icon-interactive-on-fill-default"
          })
        })
      });
    default:
      return /*#__PURE__*/_jsx(Toggle, {
        id: id,
        onChange: onToggleChange,
        checked: enabled,
        size: "extra-small",
        role: "switch"
      });
  }
};
export default ToggleUI;